import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import { formatChartValue } from '../../utils/chartFormatters';
import { transformForAttributionChart, formatTooltipCurrency } from '../../utils/wealthTracking/chartDataTransformers';
import { useCountry } from '../../CountryContext';
import { getCurrencySymbol } from '../../countryConfig';

const GrowthAttributionChart = ({ snapshots, isDark }) => {
  const { selectedCountry } = useCountry();
  const currencySymbol = getCurrencySymbol(selectedCountry);

  // Transform data for chart
  const chartData = transformForAttributionChart(snapshots);

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;

    const data = payload[0].payload;
    const raw = data._raw || {};

    return (
      <div
        className={`p-3 rounded-lg shadow-lg border ${
          isDark
            ? 'bg-gray-800 border-gray-700 text-white'
            : 'bg-white border-gray-200 text-gray-900'
        }`}
      >
        <p className="font-semibold mb-2">{data.month}</p>

        <div className="space-y-1 text-sm">
          {/* Positive contributors */}
          <div className="border-b border-gray-600 pb-2 mb-2">
            <p className="text-xs font-semibold text-green-500 mb-1">Positive</p>
            {data.contributions > 0 && (
              <div className="flex justify-between gap-4">
                <span className="text-gray-400">Contributions:</span>
                <span className="text-green-500 font-semibold">
                  +{formatTooltipCurrency(data.contributions, currencySymbol)}
                </span>
              </div>
            )}
            {data.marketGrowth > 0 && (
              <div className="flex justify-between gap-4">
                <span className="text-gray-400">Market Growth:</span>
                <span className="text-green-500 font-semibold">
                  +{formatTooltipCurrency(data.marketGrowth, currencySymbol)}
                </span>
              </div>
            )}
            {data.liabilityReduction > 0 && (
              <div className="flex justify-between gap-4">
                <span className="text-gray-400">Debt Paydown:</span>
                <span className="text-purple-500 font-semibold">
                  +{formatTooltipCurrency(data.liabilityReduction, currencySymbol)}
                </span>
              </div>
            )}
          </div>

          {/* Negative contributors */}
          {(data.withdrawals > 0 || data.marketLosses > 0 || data.interestPaid > 0) && (
            <div className="border-b border-gray-600 pb-2 mb-2">
              <p className="text-xs font-semibold text-red-500 mb-1">Negative</p>
              {data.withdrawals > 0 && (
                <div className="flex justify-between gap-4">
                  <span className="text-gray-400">Withdrawals:</span>
                  <span className="text-red-500 font-semibold">
                    -{formatTooltipCurrency(data.withdrawals, currencySymbol)}
                  </span>
                </div>
              )}
              {data.marketLosses > 0 && (
                <div className="flex justify-between gap-4">
                  <span className="text-gray-400">Market Losses:</span>
                  <span className="text-red-500 font-semibold">
                    -{formatTooltipCurrency(data.marketLosses, currencySymbol)}
                  </span>
                </div>
              )}
              {data.interestPaid > 0 && (
                <div className="flex justify-between gap-4">
                  <span className="text-gray-400">Interest Paid:</span>
                  <span className="text-red-500 font-semibold">
                    -{formatTooltipCurrency(data.interestPaid, currencySymbol)}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Net change */}
          <div className="flex justify-between gap-4 pt-2 border-t border-gray-600">
            <span className="font-semibold">Net Change:</span>
            <span
              className={`font-bold ${
                data.netChange >= 0 ? 'text-green-500' : 'text-red-500'
              }`}
            >
              {data.netChange >= 0 ? '+' : ''}
              {formatTooltipCurrency(data.netChange, currencySymbol)}
            </span>
          </div>
        </div>
      </div>
    );
  };

  if (!chartData || chartData.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        <p>No data available. Create snapshots to see growth attribution!</p>
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={350}>
      <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
        <CartesianGrid
          strokeDasharray="3 3"
          stroke={isDark ? '#374151' : '#e5e7eb'}
        />
        <XAxis
          dataKey="month"
          stroke={isDark ? '#9ca3af' : '#6b7280'}
          tick={{ fill: isDark ? '#9ca3af' : '#6b7280' }}
        />
        <YAxis
          stroke={isDark ? '#9ca3af' : '#6b7280'}
          tick={{ fill: isDark ? '#9ca3af' : '#6b7280' }}
          tickFormatter={(value) => formatChartValue(value, currencySymbol)}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend
          wrapperStyle={{ color: isDark ? '#9ca3af' : '#6b7280' }}
        />

        {/* Positive contributors - stacked */}
        <Bar
          dataKey="contributions"
          stackId="positive"
          fill="#3b82f6"
          name="Contributions"
        />
        <Bar
          dataKey="marketGrowth"
          stackId="positive"
          fill="#10b981"
          name="Market Growth"
        />
        <Bar
          dataKey="liabilityReduction"
          stackId="positive"
          fill="#8b5cf6"
          name="Debt Paydown"
        />

        {/* Negative contributors - stacked separately */}
        <Bar
          dataKey="withdrawals"
          stackId="negative"
          fill="#ef4444"
          name="Withdrawals"
        />
        <Bar
          dataKey="marketLosses"
          stackId="negative"
          fill="#f97316"
          name="Market Losses"
        />

        {/* Reference line at zero */}
        <ReferenceLine y={0} stroke={isDark ? '#6b7280' : '#9ca3af'} />
      </BarChart>
    </ResponsiveContainer>
  );
};

export default GrowthAttributionChart;
